import { authClient } from "~/lib/auth-client";
import { redirect } from "@tanstack/react-router";

/**
 * Client-side positive-session cache to avoid a network round-trip on every
 * authenticated route navigation. Null/no-user results are deliberately not
 * cached because they can be transient immediately after login navigation.
 */
const SESSION_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
type AuthSessionData = Awaited<ReturnType<typeof authClient.getSession>>["data"];
let cachedSession: { data: AuthSessionData; ts: number } | null = null;

export type ConfirmFreshSessionOptions = {
  attempts?: number;
  retryDelayMs?: number;
};

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Confirm that the browser can read the just-created Better Auth session from
 * the server before navigating into a protected route. This intentionally
 * populates only positive session cache entries; null/no-user responses remain
 * uncached because they can be transient while sign-in cookies settle.
 */
export async function confirmFreshSession({
  attempts = 3,
  retryDelayMs = 250,
}: ConfirmFreshSessionOptions = {}): Promise<AuthSessionData | null> {
  const boundedAttempts = Math.max(1, attempts);

  for (let attempt = 0; attempt < boundedAttempts; attempt += 1) {
    try {
      const session = await authClient.getSession({
        fetchOptions: { cache: "no-store" },
      });

      if (session.data?.user) {
        cachedSession = { data: session.data, ts: Date.now() };
        return session.data;
      }
    } catch {
      // Treat transient confirmation failures like no-user responses. The login
      // page will stay put with a safe error if all bounded attempts fail.
    }

    if (attempt < boundedAttempts - 1 && retryDelayMs > 0) {
      await delay(retryDelayMs);
    }
  }

  cachedSession = null;
  return null;
}

/**
 * Shared auth guard for route `beforeLoad`.
 * Checks the session and redirects to login if unauthenticated.
 * Safe for SSR — skips the check when `window` is undefined.
 */
export async function requireAuth() {
  if (typeof window !== "undefined") {
    // Return only fresh positive sessions from cache. A null/no-user result can
    // be a transient false negative immediately after login cookie propagation,
    // so never reuse it across navigations.
    if (
      cachedSession?.data?.user &&
      Date.now() - cachedSession.ts < SESSION_CACHE_TTL_MS
    ) {
      return cachedSession.data;
    }

    const session = await authClient.getSession({
      fetchOptions: { cache: "no-store" },
    });

    if (!session.data?.user) {
      cachedSession = null;
      throw redirect({ to: "/auth/login" });
    }

    cachedSession = { data: session.data, ts: Date.now() };
    return session.data;
  }
}

/** Prime the positive session cache after a fresh server-readable confirmation. */
export function primeSessionCache(data: AuthSessionData) {
  if (data?.user) {
    cachedSession = { data, ts: Date.now() };
  }
}

/** Clear the positive session cache (call on sign-in/sign-out). */
export function clearSessionCache() {
  cachedSession = null;
}
